/**
 * Leaderboard Page - Top performers tracked in real-time
 *
 * Premium design with micro-animations and interactions:
 * - Staggered card entrance animations
 * - Hover effects with glow and scale
 * - Animated rank badges with shine effects
 * - Metric toggle with satisfying feedback
 * - Animated number counters
 * - Trophy/medal animations
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FilterBar } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";

// Types
interface CloserSummary {
  id: string;
  closerName: string;
  totalOverallCash: number;
  totalCashCollected: number;
  totalRevenueGenerated: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgOrderValue: number;
  callCount: number;
  callsClosed: number;
  orderCount: number;
  closeEligibleCount: number;
  closeRate: number;
  callsDue: number;
  showRate: number;
}

interface SetterSummary {
  id: string;
  setterName: string;
  totalOverallCash: number;
  totalCashCollected: number;
  avgCashPerCall: number;
  callCount: number;
  callsDue: number;
  showRate: number;
}

interface FilterOptions {
  platforms: string[];
  coaches: string[];
  closers: string[];
  setters: string[];
  contents: string[];
}

interface LeaderboardData {
  topClosers: CloserSummary[];
  topSetters: SetterSummary[];
  filterOptions: FilterOptions;
}

// Animated number counter hook
function useAnimatedNumber(value: number, duration = 600): number {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValue = useRef(value);

  useEffect(() => {
    if (previousValue.current === value) return;

    const startValue = previousValue.current;
    const endValue = value;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (endValue - startValue) * easeOut;

      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        previousValue.current = endValue;
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return displayValue;
}

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Metric toggle types
type CloserMetric = "cash" | "totalCash" | "aov" | "cashPerCall" | "closeRate" | "showRate";
type SetterMetric = "cash" | "totalCash" | "cashPerCall" | "calls" | "showRate";

// Animated Crown Icon with shimmer
function CrownIcon({ className }: { className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 24 24" fill="currentColor" className="relative z-10">
        <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
      </svg>
      {/* Shimmer overlay */}
      <div
        className="absolute inset-0 z-20 opacity-60"
        style={{
          background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 3s infinite linear",
        }}
      />
    </div>
  );
}

// Trophy icon with bounce animation
function TrophyIcon({ color, isHovered }: { color: string; isHovered: boolean }) {
  return (
    <div
      className="transition-transform duration-300"
      style={{
        transform: isHovered ? "scale(1.2) rotate(-5deg)" : "scale(1)",
      }}
    >
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24l-7.19-.61L12 2z">
          <animate
            attributeName="opacity"
            values={isHovered ? "1;0.7;1" : "1"}
            dur="0.5s"
            repeatCount={isHovered ? "indefinite" : "0"}
          />
        </path>
      </svg>
    </div>
  );
}

// Metric toggle button with ripple effect
function MetricButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const [ripple, setRipple] = useState<{ x: number; y: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setRipple({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
        setTimeout(() => setRipple(null), 500);
      }
      onClick();
    },
    [onClick]
  );

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      className={`
        relative overflow-hidden px-2 py-1 rounded-md text-[11px] font-semibold tracking-wide
        transition-all duration-200 ease-out
        ${
          active
            ? "bg-[var(--brand-primary)] text-white shadow-lg shadow-[var(--brand-primary)]/30"
            : "bg-muted/20 text-foreground/60 hover:bg-muted/30 hover:text-foreground/90"
        }
      `}
      style={{
        transform: active ? "scale(1.02)" : "scale(1)",
      }}
    >
      <span className="relative z-10">{label}</span>

      {/* Active indicator glow */}
      {active && (
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: "radial-gradient(circle at center, var(--brand-primary) 0%, transparent 70%)",
          }}
        />
      )}

      {/* Ripple effect */}
      {ripple && (
        <span
          className="absolute rounded-full bg-white/30 animate-ping"
          style={{
            left: ripple.x - 10,
            top: ripple.y - 10,
            width: 20,
            height: 20,
          }}
        />
      )}
    </button>
  );
}

// Animated rank badge with shine effect
function RankBadge({ rank, isHovered }: { rank: number; isHovered: boolean }) {
  const colors = {
    1: { bg: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)", text: "#1a1a1a", glow: "#FFD700" },
    2: { bg: "linear-gradient(135deg, #E8E8E8 0%, #B8B8B8 50%, #E8E8E8 100%)", text: "#1a1a1a", glow: "#C0C0C0" },
    3: { bg: "linear-gradient(135deg, #CD7F32 0%, #8B5A2B 50%, #CD7F32 100%)", text: "#fff", glow: "#CD7F32" },
  };

  const config = colors[rank as keyof typeof colors] || { bg: "rgba(255,255,255,0.1)", text: "#888", glow: "transparent" };

  return (
    <div
      className="relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300"
      style={{
        background: config.bg,
        color: config.text,
        boxShadow: isHovered && rank <= 3 ? `0 0 20px ${config.glow}60, 0 0 40px ${config.glow}30` : "none",
        transform: isHovered ? "scale(1.1)" : "scale(1)",
      }}
    >
      {/* Shine overlay for top 3 */}
      {rank <= 3 && (
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)",
            backgroundSize: "200% 100%",
            animation: isHovered ? "shimmer 1s infinite linear" : "none",
          }}
        />
      )}
      <span className="relative z-10">{rank}</span>
    </div>
  );
}

// Enhanced ranking card with animations
function RankingCard({
  rank,
  name,
  subtitle,
  primaryValue,
  secondaryValue,
  index,
  isVisible,
}: {
  rank: number;
  name: string;
  subtitle: string;
  primaryValue: string;
  secondaryValue?: string;
  index: number;
  isVisible: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const getMedalColor = () => {
    if (rank === 1) return "#FFD700";
    if (rank === 2) return "#C0C0C0";
    if (rank === 3) return "#CD7F32";
    return "transparent";
  };

  const getCardGradient = () => {
    if (rank === 1) return "linear-gradient(135deg, rgba(255,215,0,0.35) 0%, rgba(255,165,0,0.15) 100%)";
    if (rank === 2) return "linear-gradient(135deg, rgba(192,192,192,0.28) 0%, rgba(128,128,128,0.10) 100%)";
    if (rank === 3) return "linear-gradient(135deg, rgba(205,127,50,0.28) 0%, rgba(139,90,43,0.10) 100%)";
    return "transparent";
  };

  const getBorderColor = () => {
    if (isHovered && rank === 1) return "rgba(255,215,0,0.6)";
    if (isHovered && rank === 2) return "rgba(192,192,192,0.5)";
    if (isHovered && rank === 3) return "rgba(205,127,50,0.5)";
    if (rank <= 3) return "rgba(255,255,255,0.1)";
    return "rgba(255,255,255,0.05)";
  };

  return (
    <div
      className="relative rounded-xl p-4 transition-all duration-300 cursor-default"
      style={{
        background: getCardGradient(),
        border: `1px solid ${getBorderColor()}`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? isHovered
            ? "translateY(-2px) scale(1.01)"
            : "translateY(0) scale(1)"
          : "translateY(20px) scale(0.95)",
        transitionDelay: isVisible ? `${index * 60}ms` : "0ms",
        boxShadow: isHovered
          ? `0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px ${getBorderColor()}`
          : "0 2px 8px rgba(0,0,0,0.1)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow effect for top 3 on hover */}
      {isHovered && rank <= 3 && (
        <div
          className="absolute inset-0 rounded-xl opacity-20 pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${getMedalColor()} 0%, transparent 70%)`,
          }}
        />
      )}

      <div className="relative flex items-center gap-4">
        <RankBadge rank={rank} isHovered={isHovered} />

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground truncate tracking-tight">{name}</p>
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
        </div>

        <div className="text-right flex items-center gap-3">
          <div>
            <p
              className="font-bold text-lg tracking-tight"
              style={{ color: "#4ade80" }}
            >
              {primaryValue}
            </p>
            {secondaryValue && (
              <p className="text-xs text-muted-foreground">{secondaryValue}</p>
            )}
          </div>

          {rank <= 3 && <TrophyIcon color={getMedalColor()} isHovered={isHovered} />}
        </div>
      </div>
    </div>
  );
}

// Premium loading skeleton
function LoadingSkeleton() {
  return (
    <div className="space-y-8">
      {/* Header skeleton */}
      <div className="text-center py-12">
        <div
          className="w-20 h-20 mx-auto rounded-2xl mb-6"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite linear",
          }}
        />
        <div
          className="h-12 w-72 mx-auto rounded-lg mb-3"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite linear",
          }}
        />
        <div
          className="h-4 w-56 mx-auto rounded"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite linear",
            animationDelay: "0.1s",
          }}
        />
      </div>

      {/* Filter bar skeleton */}
      <div
        className="h-16 rounded-xl"
        style={{
          background: "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 100%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite linear",
        }}
      />

      {/* Cards skeleton */}
      <div className="grid gap-6 lg:grid-cols-2">
        {[0, 1].map((col) => (
          <div key={col} className="rounded-2xl border border-border p-6 bg-muted/5">
            <div
              className="h-8 w-48 rounded-lg mb-6"
              style={{
                background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite linear",
              }}
            />
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-xl"
                  style={{
                    background: "linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite linear",
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Section header with animated gradient
function SectionHeader({
  icon,
  title,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconColor: string;
  children: React.ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative p-5 border-b border-white/5 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated gradient background */}
      <div
        className="absolute inset-0 opacity-30 transition-opacity duration-500"
        style={{
          background: `linear-gradient(135deg, ${iconColor}20 0%, transparent 60%)`,
          opacity: isHovered ? 0.4 : 0.2,
        }}
      />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300"
            style={{
              background: `linear-gradient(135deg, ${iconColor}30 0%, ${iconColor}10 100%)`,
              boxShadow: isHovered ? `0 0 20px ${iconColor}30` : "none",
              transform: isHovered ? "scale(1.05)" : "scale(1)",
            }}
          >
            {icon}
          </div>
          <h3 className="text-lg font-bold text-foreground tracking-tight">{title}</h3>
        </div>

        <div className="flex gap-1.5">{children}</div>
      </div>
    </div>
  );
}

export function Leaderboard() {
  // Use global date range from context (synced with header date picker)
  const { dateRange: globalDateRange, formatForApi } = useDateRange();

  // Use shared filters from context (synced with URL via nuqs)
  const {
    dateField,
    setDateField,
    platforms,
    setPlatforms,
    coaches,
    setCoaches,
    closers,
    setClosers,
    setters,
    setSetters,
    contents,
    setContents,
    clearAllFilters,
  } = useFilters();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Metric toggle state
  const [closerMetric, setCloserMetric] = useState<CloserMetric>("cash");
  const [setterMetric, setSetterMetric] = useState<SetterMetric>("cash");

  // Trigger visibility animation after data loads
  useEffect(() => {
    if (data && !isLoading) {
      setIsVisible(false);
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [data, isLoading, closerMetric, setterMetric]);

  // Fetch data when filters change (using global date range and shared filters)
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Use global date range from context
        const apiDates = formatForApi();
        const params = new URLSearchParams();
        params.set("dateFrom", apiDates.startDate);
        params.set("dateTo", apiDates.endDate);
        params.set("dateField", dateField);
        platforms.forEach((p) => params.append("platform", p));
        coaches.forEach((c) => params.append("coach", c));
        closers.forEach((c) => params.append("closer", c));
        setters.forEach((s) => params.append("setter", s));
        contents.forEach((c) => params.append("content", c));

        const response = await fetch(`/api/leaderboard?${params.toString()}`);
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || "Failed to fetch leaderboard data");
        }

        setData(result.data);
      } catch (err) {
        console.error("Error fetching leaderboard:", err);
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [globalDateRange, formatForApi, dateField, platforms, coaches, closers, setters, contents]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    if (!data?.filterOptions) return;
    const opts = data.filterOptions;

    // Skip if filterOptions haven't loaded yet
    if (!opts.platforms?.length && !opts.coaches?.length && !opts.closers?.length) {
      return;
    }

    let hasChanges = false;

    // Check platform filter
    if (platforms.length > 0 && opts.platforms?.length > 0) {
      const validPlatforms = platforms.filter(p => opts.platforms.includes(p));
      if (validPlatforms.length !== platforms.length) {
        setPlatforms(validPlatforms);
        hasChanges = true;
      }
    }

    // Check coach filter
    if (coaches.length > 0 && opts.coaches?.length > 0) {
      const validCoaches = coaches.filter(c => opts.coaches.includes(c));
      if (validCoaches.length !== coaches.length) {
        setCoaches(validCoaches);
        hasChanges = true;
      }
    }

    // Check closer filter
    if (closers.length > 0 && opts.closers?.length > 0) {
      const validClosers = closers.filter(c => opts.closers.includes(c));
      if (validClosers.length !== closers.length) {
        setClosers(validClosers);
        hasChanges = true;
      }
    }

    // Check setter filter
    if (setters.length > 0 && opts.setters?.length > 0) {
      const validSetters = setters.filter(s => opts.setters.includes(s));
      if (validSetters.length !== setters.length) {
        setSetters(validSetters);
        hasChanges = true;
      }
    }

    // Check content filter
    if (contents.length > 0 && opts.contents?.length > 0) {
      // contents from API have value/label format
      const contentValues = opts.contents.map((c: string | { value: string }) => typeof c === "string" ? c : c.value);
      const validContents = contents.filter(c => contentValues.includes(c));
      if (validContents.length !== contents.length) {
        setContents(validContents);
        hasChanges = true;
      }
    }
  }, [data?.filterOptions, platforms, coaches, closers, setters, contents, setPlatforms, setCoaches, setClosers, setSetters, setContents]);

  // Sort closers based on selected metric
  const sortedClosers = useMemo(() => {
    if (!data?.topClosers) return [];
    const sorted = [...data.topClosers];

    switch (closerMetric) {
      case "cash":
        return sorted.sort((a, b) => b.totalCashCollected - a.totalCashCollected);
      case "totalCash":
        return sorted.sort((a, b) => b.totalOverallCash - a.totalOverallCash);
      case "aov":
        return sorted.sort((a, b) => b.avgOrderValue - a.avgOrderValue);
      case "cashPerCall":
        return sorted.sort((a, b) => b.avgCashPerCall - a.avgCashPerCall);
      case "closeRate":
        return sorted.sort((a, b) => b.closeRate - a.closeRate);
      case "showRate":
        return sorted.sort((a, b) => b.showRate - a.showRate);
      default:
        return sorted;
    }
  }, [data?.topClosers, closerMetric]);

  // Sort setters based on selected metric
  const sortedSetters = useMemo(() => {
    if (!data?.topSetters) return [];
    const sorted = [...data.topSetters];

    switch (setterMetric) {
      case "cash":
        return sorted.sort((a, b) => b.totalCashCollected - a.totalCashCollected);
      case "totalCash":
        return sorted.sort((a, b) => b.totalOverallCash - a.totalOverallCash);
      case "cashPerCall":
        return sorted.sort((a, b) => b.avgCashPerCall - a.avgCashPerCall);
      case "calls":
        return sorted.sort((a, b) => b.callCount - a.callCount);
      case "showRate":
        return sorted.sort((a, b) => b.showRate - a.showRate);
      default:
        return sorted;
    }
  }, [data?.topSetters, setterMetric]);

  // Get display values
  const getCloserPrimaryValue = (closer: CloserSummary): string => {
    switch (closerMetric) {
      case "cash":
        return formatCurrency(closer.totalCashCollected);
      case "totalCash":
        return formatCurrency(closer.totalOverallCash);
      case "aov":
        return formatCurrency(closer.avgOrderValue);
      case "cashPerCall":
        return formatCurrency(closer.avgCashPerCall);
      case "closeRate":
        return formatPercent(closer.closeRate);
      case "showRate":
        return formatPercent(closer.showRate);
      default:
        return formatCurrency(closer.totalCashCollected);
    }
  };

  const getCloserSubtitle = (closer: CloserSummary): string => {
    switch (closerMetric) {
      case "cash":
        return `${closer.callsClosed} closes / ${closer.callCount} calls`;
      case "totalCash":
        return `incl. ${formatCurrency(closer.totalOverallCash - closer.totalCashCollected)} MRR`;
      case "aov":
        return `${closer.orderCount} orders`;
      case "cashPerCall":
        return `${closer.callCount} calls`;
      case "closeRate":
        return `${closer.callsClosed}/${closer.closeEligibleCount} closed`;
      case "showRate":
        return `${closer.callCount}/${closer.callsDue} showed`;
      default:
        return `${closer.callsClosed} closes / ${closer.callCount} calls`;
    }
  };

  const getSetterPrimaryValue = (setter: SetterSummary): string => {
    switch (setterMetric) {
      case "cash":
        return formatCurrency(setter.totalCashCollected);
      case "totalCash":
        return formatCurrency(setter.totalOverallCash);
      case "cashPerCall":
        return formatCurrency(setter.avgCashPerCall);
      case "calls":
        return `${setter.callCount} calls`;
      case "showRate":
        return formatPercent(setter.showRate);
      default:
        return formatCurrency(setter.totalCashCollected);
    }
  };

  const getSetterSubtitle = (setter: SetterSummary): string => {
    switch (setterMetric) {
      case "cash":
        return `${setter.callCount} calls`;
      case "totalCash":
        return `incl. ${formatCurrency(setter.totalOverallCash - setter.totalCashCollected)} MRR`;
      case "cashPerCall":
        return `${setter.callCount} calls`;
      case "calls":
        return `${setter.showRate.toFixed(0)}% show rate`;
      case "showRate":
        return `${setter.callCount}/${setter.callsDue} showed`;
      default:
        return `${setter.callCount} calls`;
    }
  };

  // Build filter options
  const filterOptions = data?.filterOptions || {
    platforms: [],
    coaches: [],
    closers: [],
    setters: [],
    contents: [],
  };

  const filters = [
    {
      key: "content",
      label: "Select Content",
      options: filterOptions.contents.map((c) => ({ value: c, label: c })),
      value: contents,
      onChange: (v: string | string[]) => setContents(v as string[]),
      multiple: true,
    },
    {
      key: "platform",
      label: "Select Platforms",
      options: filterOptions.platforms.map((p) => ({ value: p, label: p })),
      value: platforms,
      onChange: (v: string | string[]) => setPlatforms(v as string[]),
      multiple: true,
    },
    {
      key: "coach",
      label: "Select Coaches",
      options: filterOptions.coaches.map((c) => ({ value: c, label: c })),
      value: coaches,
      onChange: (v: string | string[]) => setCoaches(v as string[]),
      multiple: true,
    },
    {
      key: "closer",
      label: "Select Closers",
      options: filterOptions.closers.map((c) => ({ value: c, label: c })),
      value: closers,
      onChange: (v: string | string[]) => setClosers(v as string[]),
      multiple: true,
    },
    {
      key: "setter",
      label: "Select Setters",
      options: filterOptions.setters.map((s) => ({ value: s, label: s })),
      value: setters,
      onChange: (v: string | string[]) => setSetters(v as string[]),
      multiple: true,
    },
  ];

  // Clear all filters (date range is managed globally by the header picker)
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Inject keyframes
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-6px); }
      }
      @keyframes pulse-glow {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Loading state
  if (isLoading && !data) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <CrownIcon className="w-20 h-20 mx-auto text-yellow-500 mb-6" />
          <h1
            className="text-5xl font-black tracking-tight mb-3"
            style={{
              background: "linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            LEADERBOARD
          </h1>
          <p className="text-muted-foreground">Top performers tracked in real-time</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center">
          <p className="text-red-400 font-medium mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-12">
        <div
          className="relative inline-block"
          style={{ animation: "float 4s ease-in-out infinite" }}
        >
          <CrownIcon className="w-20 h-20 mx-auto text-yellow-500 drop-shadow-lg" />
          {/* Glow behind crown */}
          <div
            className="absolute inset-0 -z-10 blur-2xl opacity-50"
            style={{
              background: "radial-gradient(circle, #FFD700 0%, transparent 70%)",
            }}
          />
        </div>

        <h1
          className="text-5xl font-black tracking-tight mt-6 mb-3"
          style={{
            background: "linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 0 40px rgba(var(--brand-primary), 0.3)",
          }}
        >
          LEADERBOARD
        </h1>
        <p className="text-muted-foreground text-lg">Top performers tracked in real-time</p>
      </div>

      {/* Filter Bar - date range is controlled by the global header picker */}
      <FilterBar
        showDateFieldToggle
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={filters}
        onClearAll={handleClearAll}
      />

      {/* Rankings Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Closers */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <SectionHeader
            icon={
              <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24l-7.19-.61L12 2z" />
              </svg>
            }
            title="Top Closers"
            iconColor="#FFD700"
          >
            <MetricButton label="New Cash" active={closerMetric === "cash"} onClick={() => setCloserMetric("cash")} />
            <MetricButton label="Total Cash" active={closerMetric === "totalCash"} onClick={() => setCloserMetric("totalCash")} />
            <MetricButton label="AOV" active={closerMetric === "aov"} onClick={() => setCloserMetric("aov")} />
            <MetricButton label="$/Call" active={closerMetric === "cashPerCall"} onClick={() => setCloserMetric("cashPerCall")} />
            <MetricButton label="Close %" active={closerMetric === "closeRate"} onClick={() => setCloserMetric("closeRate")} />
            <MetricButton label="Show %" active={closerMetric === "showRate"} onClick={() => setCloserMetric("showRate")} />
          </SectionHeader>

          <div className="p-4 space-y-2">
            {sortedClosers.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No closers found for the selected filters</p>
            ) : (
              sortedClosers.map((closer, index) => (
                <RankingCard
                  key={closer.id}
                  rank={index + 1}
                  name={closer.closerName}
                  subtitle={getCloserSubtitle(closer)}
                  primaryValue={getCloserPrimaryValue(closer)}
                  secondaryValue={
                    closerMetric === "totalCash"
                      ? formatCurrency(closer.totalCashCollected) + " new cash"
                      : formatCurrency(closer.totalOverallCash) + " w/ MRR"
                  }
                  index={index}
                  isVisible={isVisible}
                />
              ))
            )}
          </div>
        </div>

        {/* Top Setters */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <SectionHeader
            icon={
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24l-7.19-.61L12 2z" />
              </svg>
            }
            title="Top Setters"
            iconColor="#9CA3AF"
          >
            <MetricButton label="New Cash" active={setterMetric === "cash"} onClick={() => setSetterMetric("cash")} />
            <MetricButton label="Total Cash" active={setterMetric === "totalCash"} onClick={() => setSetterMetric("totalCash")} />
            <MetricButton label="$/Call" active={setterMetric === "cashPerCall"} onClick={() => setSetterMetric("cashPerCall")} />
            <MetricButton label="Calls" active={setterMetric === "calls"} onClick={() => setSetterMetric("calls")} />
            <MetricButton label="Show %" active={setterMetric === "showRate"} onClick={() => setSetterMetric("showRate")} />
          </SectionHeader>

          <div className="p-4 space-y-2">
            {sortedSetters.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No setters found for the selected filters</p>
            ) : (
              sortedSetters.map((setter, index) => (
                <RankingCard
                  key={setter.id}
                  rank={index + 1}
                  name={setter.setterName}
                  subtitle={getSetterSubtitle(setter)}
                  primaryValue={getSetterPrimaryValue(setter)}
                  secondaryValue={
                    setterMetric === "totalCash"
                      ? formatCurrency(setter.totalCashCollected) + " new cash"
                      : formatCurrency(setter.totalOverallCash) + " w/ MRR"
                  }
                  index={index}
                  isVisible={isVisible}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
